import { FileExcelOutlined, MenuFoldOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import type { BaseTransactionQueryParams, TransactionDetail } from '@api/useFreight/types'
import { useTransactions } from '@api/useFreight/useTransactions'
import {
  BBDTag,
  Horizontal,
  MaterialIcon,
  type MaterialSymbolIcon,
  RangePicker,
  Texto,
  useLocalStorage,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { Radio, Select, Tabs } from 'antd'
import type { TabsProps } from 'antd'
import dayjs from 'dayjs'
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { LINE_ITEM_STATUS_CONFIG } from '../shared/LineItems/components'
import { ByOrderView } from './ByOrderView'
import { FlatView } from './FlatView'
import { DownloadButton } from '../../_SiteWide/Grid/ActionButtons/DownloadButton'
import type { GridApi } from 'ag-grid-community'
// Using native TypeScript optional types instead of UndefinedDate

// Helper to get default date range for Posted view (matches hook)
function getDefaultPostedDateRange(): [Date, Date] {
  const end = new Date()
  const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000)
  return [start, end]
}

// Helper to get dashboard date range (30 days back)
function getDashboardDateRange(): [Date, Date] {
  const today = new Date()
  const startDate30DaysAgo = dayjs(today).subtract(30, 'day').toDate()
  return [startDate30DaysAgo, today]
}

const handleSearchFilters = (filters: URLSearchParams) => {
  const searchParamsObject = Object.fromEntries(filters.entries())
  searchParamsObject.status = '' // Handled by state
  searchParamsObject.view = '' // Handled by state
  searchParamsObject.type = '' // Filtered client-side for now
  searchParamsObject.costRevenue = '' // Handled by state for data fetching
  return searchParamsObject
}

// Type for status counts
interface StatusCounts {
  all: number
  Error: number
  'Needs Approval': number
  Ready: number
  Estimated: number
  Cost: number
  Manual: number
  Posted: number
  [key: string]: number // Index signature for dynamic access
}

export function FreightLineItems() {
  const [searchParams] = useSearchParams()

  // View mode state
  const { value: viewMode, setValue: setViewMode } = useLocalStorage<'flat' | 'byOrder'>(
    'freight-line-items-view',
    () => {
      const hasQueryParams = searchParams.toString().length > 0
      return hasQueryParams ? 'byOrder' : 'flat'
    }
  )

  // Top Level Tab State
  const [activeTopTab, setActiveTopTab] = useState<'actual' | 'estimated'>('actual')

  // Date Range State (using dayjs for compatibility with RangePicker)
  const [dateRange, setDateRange] = useState<[Date?, Date?]>(() => {
    const hasQueryParams = searchParams.toString().length > 0
    return hasQueryParams ? getDashboardDateRange() : getDefaultPostedDateRange()
  })

  // Inner Status Tab State
  const { value: activeStatus, setValue: setActiveStatus } = useLocalStorage<string>('freight-line-items-status', 'all') // 'all' refers to 'All Open'

  // Cost/Revenue State
  const { value: costRevenue, setValue: setCostRevenue } = useLocalStorage<
    'Cost' | 'Revenue' | null // Allow null initially
  >('freight-line-items-cost-revenue', 'Revenue') // Default to Revenue

  // Server-side Filter State (derived from URL params initially)
  const [serverFilters, setServerFilters] = useState(() => handleSearchFilters(searchParams))

  // --- Effects --- //

  // Initialize state from URL Search Params
  useEffect(() => {
    const urlStatus = searchParams.get('status')
    const urlView = searchParams.get('view')
    const urlType = searchParams.get('type')
    const urlCostRevenue = searchParams.get('costRevenue')

    if (urlCostRevenue === 'Cost' || urlCostRevenue === 'Revenue') {
      setCostRevenue(urlCostRevenue)
    }

    if (urlStatus) {
      setActiveStatus(urlStatus === 'open' ? 'all' : urlStatus)
    }
    if (urlView) {
      setViewMode(urlView as 'flat' | 'byOrder')
    }

    // Update serverFilters if searchParams change
    setServerFilters(handleSearchFilters(searchParams))
  }, [searchParams, setActiveStatus, setViewMode, setCostRevenue])

  // Reset inner status tab if top tab changes
  useEffect(() => {
    if (activeTopTab === 'estimated') {
      setActiveStatus('estimated') // Force status to 'estimated'
    } else {
      // If switching back to actual, and current status is 'estimated',
      // reset to 'all' (All Open)
      if (activeStatus === 'estimated') {
        setActiveStatus('all')
      }
    }
  }, [activeTopTab, activeStatus, setActiveStatus])

  // --- Utility Hooks --- //
  const gridAPIRef: React.RefObject<GridApi<TransactionDetail>> = useRef(null)
  const { formatCurrency } = useFormatter()
  const { updateSetting } = useUserSettings()

  // --- Data Fetching --- //
  const { useEstimatedTransactionsQuery, useOpenTransactionsQuery, usePostedTransactionsQuery } = useTransactions()

  const queryParams = {
    costRevenue: costRevenue as 'Cost' | 'Revenue',
    filter: serverFilters,
    from_date: dateRange[0],
    to_date: dateRange[1],
  }

  // Estimated Query - Enabled only when its tab is active
  const estimatedQuery = useEstimatedTransactionsQuery({
    ...queryParams,
  })

  // Open Query - Enabled whenever 'Actual' tab is active for counts
  const openQuery = useOpenTransactionsQuery({
    ...queryParams,
  })

  const postedQuery = usePostedTransactionsQuery({
    ...queryParams,
  })

  // Determine current data source and loading state FOR THE GRID
  const {
    data: currentTransactionsData, // Data used by the grid based on active tabs
    isLoading: isCurrentLoading,
    isFetching: isCurrentFetching,
  } = useMemo(() => {
    // This logic remains the same, deciding what data to *display* in the grid
    if (activeTopTab === 'estimated') {
      return {
        data: estimatedQuery.data,
        isLoading: estimatedQuery.isPending,
        isFetching: estimatedQuery.isFetching,
      }
    }
    if (activeStatus === 'posted') {
      return {
        data: postedQuery.data,
        isLoading: postedQuery.isPending,
        isFetching: postedQuery.isFetching,
      }
    }
    // Covers 'all' and other open statuses
    return {
      data: openQuery.data,
      isLoading: openQuery.isPending,
      isFetching: openQuery.isFetching,
    }
  }, [
    activeTopTab,
    activeStatus,
    estimatedQuery.data,
    estimatedQuery.isPending,
    estimatedQuery.isFetching,
    openQuery.data,
    openQuery.isPending,
    openQuery.isFetching,
    postedQuery.data,
    postedQuery.isPending,
    postedQuery.isFetching,
  ])

  // --- Data Processing --- //

  // Get the correct data based on view mode
  const currentViewData = useMemo(() => {
    if (!currentTransactionsData) return { rows: [], details: [] }

    // The hooks now return { rows, details, params }
    // 'details' are pre-flattened
    if (viewMode === 'flat') {
      return {
        rows: [], // Not used in flat view
        details: currentTransactionsData.details || [],
      }
    }
    // By Order view uses rows directly
    return {
      rows: currentTransactionsData.rows || [],
      details: [], // Not used in by-order view
    }
  }, [currentTransactionsData, viewMode])

  // Client-side filtering (moved from shared function)
  const flatFilteredData = useMemo(() => {
    if (!currentViewData.details) return []
    let filtered = [...currentViewData.details]
    const typeParam = searchParams.get('type')

    // Apply status filter (only for specific open statuses)
    if (activeStatus && activeStatus !== 'all' && activeStatus !== 'posted') {
      filtered = filtered.filter((item) => item.status === activeStatus)
    }

    // Apply type filter
    if (typeParam) {
      filtered = filtered.filter((item) => item.type?.toLowerCase() === typeParam.toLowerCase())
    }

    // Remove duplicates by ID
    return filtered.filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
    // Add activeStatus to dependencies
  }, [currentViewData.details, searchParams, activeStatus])

  const byOrderFilteredData = useMemo(() => {
    if (!currentViewData.rows) return []
    let filtered = [...currentViewData.rows]
    const typeParam = searchParams.get('type')

    // Apply status filter (only for specific open statuses)
    if (activeStatus && activeStatus !== 'all' && activeStatus !== 'posted') {
      filtered = filtered.filter((row) => row.status === activeStatus)
    }

    // Apply type filter (check details within the row)
    if (typeParam) {
      filtered = filtered.filter((row) =>
        row.details?.some(
          // Add type annotation to detail
          (detail: TransactionDetail) => detail.type.toLowerCase() === typeParam.toLowerCase()
        )
      )
    }

    // Remove duplicates by order_id
    return filtered.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.order_id === item.order_id && t.customer_id === item.customer_id)
    )
    // Add activeStatus to dependencies
  }, [currentViewData.rows, searchParams, activeStatus])

  // --- Status Counts Calculation (Using combined query data & considering viewMode) --- //
  const statusCounts = useMemo((): StatusCounts => {
    const defaultCounts: StatusCounts = {
      all: 0,
      Error: 0,
      'Needs Approval': 0,
      Ready: 0,
      Estimated: 0,
      Cost: 0,
      Manual: 0,
      Posted: 0,
    }

    // Define generic filter function
    const filterByStatus = <T extends { status: string }>(
      items: T[] | undefined, // Allow undefined input
      status: string
    ): number => {
      if (!items) return 0 // Return 0 if items is undefined
      return items.filter((item) => item.status === status).length
    }

    if (activeTopTab === 'estimated') {
      // Use estimatedQuery data for counts based on viewMode
      const estimatedRows = estimatedQuery.data?.rows
      const estimatedDetails = estimatedQuery.data?.details
      const counts = { ...defaultCounts }
      const estimatedStatusKey = 'estimated'

      // Call filterByStatus with the correct type based on viewMode
      counts.Estimated =
        viewMode === 'byOrder'
          ? filterByStatus(estimatedRows, estimatedStatusKey)
          : filterByStatus(estimatedDetails, estimatedStatusKey)

      return counts
    }
    // Use openQuery for open counts and postedQuery for posted count, based on viewMode
    const openRows = openQuery.data?.rows
    const openDetails = openQuery.data?.details
    const postedRows = postedQuery.data?.rows
    const postedDetails = postedQuery.data?.details
    const counts = { ...defaultCounts }

    // Calculate 'all' count based on viewMode
    counts.all = viewMode === 'byOrder' ? openRows?.length || 0 : openDetails?.length || 0

    // Calculate open status counts based on viewMode
    for (const [apiStatus, config] of Object.entries(LINE_ITEM_STATUS_CONFIG)) {
      const typedApiStatus = apiStatus as keyof typeof LINE_ITEM_STATUS_CONFIG
      if (typedApiStatus !== 'estimated' && typedApiStatus !== 'posted') {
        counts[config.display] =
          viewMode === 'byOrder'
            ? filterByStatus(openRows, typedApiStatus)
            : filterByStatus(openDetails, typedApiStatus)
      }
    }

    // Calculate posted count based on viewMode
    const postedStatusKey = 'posted'
    counts.Posted =
      viewMode === 'byOrder'
        ? filterByStatus(postedRows, postedStatusKey)
        : filterByStatus(postedDetails, postedStatusKey)

    return counts
  }, [
    activeTopTab,
    viewMode,
    openQuery.data?.details,
    postedQuery.data?.details,
    estimatedQuery.data?.details,
    openQuery.data?.rows,
    postedQuery.data?.rows,
    estimatedQuery.data?.rows,
  ])

  // --- Tab Items --- //
  const innerTabItems: TabsProps['items'] = useMemo(() => {
    // Filter status configs based on the top tab
    const relevantStatuses = Object.entries(LINE_ITEM_STATUS_CONFIG).filter(
      ([apiStatus]) => (activeTopTab === 'estimated' ? apiStatus === 'estimated' : apiStatus !== 'estimated') // Exclude 'estimated' for 'actual' tab
    )

    const items = relevantStatuses.map(([apiStatus, config]) => ({
      key: apiStatus,
      label: (
        <Horizontal verticalCenter horizontalCenter style={{ gap: '8px' }}>
          <MaterialIcon icon={config.icon as MaterialSymbolIcon} />
          <Texto appearance='default'>{config.display}</Texto>
          <BBDTag
            error={config.tagProps.error}
            warning={config.tagProps.warning}
            success={config.tagProps.success}
            // Cast tagProps to any to bypass theme2 type issue
            theme2={config.tagProps.theme2 ?? false}
            style={{ width: 'fit-content' }}
          >
            {/* Access count using display name */}
            {statusCounts[config.display] || 0}
          </BBDTag>
        </Horizontal>
      ),
      children: null,
    }))

    // Add 'All Open' tab if on 'actual'
    if (activeTopTab === 'actual') {
      items.unshift({
        key: 'all', // 'all' represents 'All Open'
        label: (
          <Horizontal verticalCenter horizontalCenter style={{ gap: '8px' }}>
            <MaterialIcon icon='list' />
            <Texto appearance='default'>All Open</Texto>
            <BBDTag style={{ width: 'fit-content' }}>{statusCounts.all || 0}</BBDTag>
          </Horizontal>
        ),
        children: null,
      })
    }

    return items
  }, [activeTopTab, statusCounts])

  // --- Render --- //
  return (
    <Vertical style={{ height: '100%', overflow: 'hidden' }}>
      {/* Top Level Tabs (Actual / Estimated) */}
      <Tabs
        activeKey={activeTopTab}
        onChange={(key) => setActiveTopTab(key as 'actual' | 'estimated')} // Ensure correct type
        className='px-4'
        style={{
          marginBottom: 0,
          backgroundColor: 'var(--bg-1)',
          borderTop: '1px solid var(--gray-200)',
          borderRight: '1px solid var(--gray-200)',
          borderLeft: '1px solid var(--gray-200)',
          borderRadius: '8px 8px 0 0',
        }}
        tabBarStyle={{
          marginBottom: 0,
        }}
        tabBarExtraContent={{
          right: (
            <Horizontal verticalCenter gap={8} className='pr-4'>
              {/* Cost/Revenue Selector remains here */}
              <Texto appearance='hint'>Cost/Revenue: </Texto>
              <Select
                size='small'
                value={costRevenue}
                onChange={(value) => setCostRevenue(value)} // Directly set state
                style={{ width: 120 }}
                options={[
                  { value: 'Cost', label: 'Cost' },
                  { value: 'Revenue', label: 'Revenue' },
                ]}
                allowClear={false} // Prevent null selection after initial load
              />
            </Horizontal>
          ),
        }}
        items={[
          { key: 'actual', label: 'Actual' },
          { key: 'estimated', label: 'Estimated' },
        ]}
      />

      {/* Inner Status Tabs - Conditionally Shown */}
      {activeTopTab === 'actual' && (
        <Tabs
          activeKey={activeStatus || 'all'} // Handle null/undefined activeStatus
          onChange={setActiveStatus}
          items={innerTabItems} // Use dynamically generated items
          className='px-4'
          style={{
            marginBottom: 0,
            backgroundColor: 'var(--bg-1)',
            // Removed borderTop to connect visually with top tabs
            borderRight: '1px solid var(--gray-200)',
            borderLeft: '1px solid var(--gray-200)',
            // No top radius
          }}
          tabBarStyle={{
            marginBottom: 0,
          }}
          // Date picker moved to top tabs
          tabBarExtraContent={null} // Remove extra content here
        />
      )}

      {viewMode === 'flat' ? (
        <FlatView
          actionButtons={
            <ActionButtons
              dateRange={dateRange}
              viewMode={viewMode}
              activeStatus={activeStatus}
              costRevenue={costRevenue}
              setViewMode={setViewMode}
              setDateRange={setDateRange}
              activeTopTab={activeTopTab}
              gridAPIRef={gridAPIRef as MutableRefObject<GridApi<TransactionDetail>>}
            />
          }
          gridAPIRef={gridAPIRef as MutableRefObject<GridApi<TransactionDetail>>}
          filteredData={flatFilteredData}
          updateSetting={updateSetting}
          isLoading={isCurrentLoading || isCurrentFetching}
          //@ts-ignore
          serverParams={currentTransactionsData?.params}
          //@ts-ignore
          setServerFilters={setServerFilters}
          filters={serverFilters}
        />
      ) : (
        <ByOrderView
          costRevenue={costRevenue || 'Revenue'} // Ensure non-null value
          formatCurrency={formatCurrency}
          data={byOrderFilteredData}
          isLoading={isCurrentLoading || isCurrentFetching}
          actionButtons={
            <ActionButtons
              dateRange={dateRange}
              viewMode={viewMode as 'flat' | 'byOrder'}
              gridAPIRef={gridAPIRef as MutableRefObject<GridApi<TransactionDetail>>}
              activeStatus={activeStatus}
              costRevenue={costRevenue}
              setViewMode={setViewMode}
              setDateRange={setDateRange}
              activeTopTab={activeTopTab}
            />
          }
          //@ts-ignore
          serverParams={currentTransactionsData?.params}
          //@ts-ignore
          setServerFilters={setServerFilters}
          filters={serverFilters}
          //@ts-ignore
          gridAPIRef={gridAPIRef}
        />
      )}
    </Vertical>
  )
}
const ActionButtons = ({
  dateRange,
  viewMode,
  activeStatus,
  costRevenue,
  setViewMode,
  setDateRange,
  activeTopTab,
  gridAPIRef,
}: {
  dateRange: [Date?, Date?]
  viewMode: 'flat' | 'byOrder'
  activeStatus: string | null
  costRevenue: 'Cost' | 'Revenue' | null
  setViewMode: (viewMode: 'flat' | 'byOrder') => void
  setDateRange: (dateRange: [Date?, Date?]) => void
  activeTopTab: 'actual' | 'estimated'
  gridAPIRef: MutableRefObject<GridApi<TransactionDetail>>
}) => {
  const getFileName = () => {
    const viewSuffix = viewMode === 'flat' ? 'FlatView' : 'ByOrder'
    const statusSuffix = activeStatus === 'all' ? 'AllOpen' : activeStatus
    return `FreightLineItems_${activeTopTab}_${costRevenue}_${statusSuffix}_${viewSuffix}_${dayjs().format(
      'YYYY-MM-DD'
    )}.xlsx`
  }

  return (
    <Horizontal verticalCenter gap={8}>
      {/* View Mode Selector */}
      <Radio.Group
        style={{ minWidth: 'max-content' }}
        value={viewMode}
        onChange={(e) => setViewMode(e.target.value)}
        optionType='button'
        buttonStyle='solid'
        options={[
          {
            label: 'Flat',
            value: 'flat',
            icon: <UnorderedListOutlined />,
          },
          {
            label: 'By Order',
            value: 'byOrder',
            icon: <MenuFoldOutlined />,
          },
        ].map((option) => ({
          label: (
            <>
              {option.icon}
              <span style={{ marginLeft: 8 }}>{option.label}</span>
            </>
          ),
          value: option.value,
        }))}
      />

      <DownloadButton fileName={getFileName()} gridAPIRef={gridAPIRef} />

      {/* Date Picker - Moved here */}
      <RangePicker
        inputKey='freight-line-items-date-range'
        dates={dateRange?.[0] && dateRange?.[1] ? [dateRange[0], dateRange[1]] : undefined}
        onChange={(newDates) => {
          setDateRange([newDates?.[0], newDates?.[1]])
        }}
        placement='bottomLeft'
      />
    </Horizontal>
  )
}
